import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Row, Col, FormText } from 'reactstrap';
import { isNumber, Translate, translate, ValidatedField, ValidatedForm } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { convertDateTimeFromServer, convertDateTimeToServer, displayDefaultDateTime } from 'app/shared/util/date-utils';
import { mapIdList } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { IUser } from 'app/shared/model/user.model';
import { getUsers } from 'app/modules/administration/user-management/user-management.reducer';
import { IProductDeliveryStatus } from 'app/shared/model/product-delivery-status.model';
import { getEntities as getProductDeliveryStatuses } from 'app/entities/product-delivery-status/product-delivery-status.reducer';
import { ITransactionDetails } from 'app/shared/model/transaction-details.model';
import { getEntities as getTransactionDetails } from 'app/entities/transaction-details/transaction-details.reducer';
import { IUserPurchasedPlans } from 'app/shared/model/user-purchased-plans.model';
import { getEntity, updateEntity, createEntity, reset } from './user-purchased-plans.reducer';

export const UserPurchasedPlansUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const users = useAppSelector(state => state.userManagement.users);
  const productDeliveryStatuses = useAppSelector(state => state.productDeliveryStatus.entities);
  const transactionDetails = useAppSelector(state => state.transactionDetails.entities);
  const userPurchasedPlansEntity = useAppSelector(state => state.userPurchasedPlans.entity);
  const loading = useAppSelector(state => state.userPurchasedPlans.loading);
  const updating = useAppSelector(state => state.userPurchasedPlans.updating);
  const updateSuccess = useAppSelector(state => state.userPurchasedPlans.updateSuccess);

  const handleClose = () => {
    navigate('/user-purchased-plans' + location.search);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getUsers({}));
    dispatch(getProductDeliveryStatuses({}));
    dispatch(getTransactionDetails({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    values.planEndDate = convertDateTimeToServer(values.planEndDate);
    values.planStartDate = convertDateTimeToServer(values.planStartDate);

    const entity = {
      ...userPurchasedPlansEntity,
      ...values,
      user: users.find(it => it.id.toString() === values.user.toString()),
      productDeliveryStatus: productDeliveryStatuses.find(it => it.id.toString() === values.productDeliveryStatus.toString()),
      transactionDetails: transactionDetails.find(it => it.id.toString() === values.transactionDetails.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {
          planEndDate: displayDefaultDateTime(),
        }
      : {
          ...userPurchasedPlansEntity,
          planEndDate: convertDateTimeFromServer(userPurchasedPlansEntity.planEndDate),
          user: userPurchasedPlansEntity?.user?.id,
          productDeliveryStatus: userPurchasedPlansEntity?.productDeliveryStatus?.id,
          transactionDetails: userPurchasedPlansEntity?.transactionDetails?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="fitengineerBackendV2App.userPurchasedPlans.home.createOrEditLabel" data-cy="UserPurchasedPlansCreateUpdateHeading">
            <Translate contentKey="fitengineerBackendV2App.userPurchasedPlans.home.createOrEditLabel">
              Create or edit a UserPurchasedPlans
            </Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="user-purchased-plans-id"
                  label={translate('global.field.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('fitengineerBackendV2App.userPurchasedPlans.planStartDate')}
                id="user-purchased-plans-planStartDate"
                name="planStartDate"
                data-cy="planStartDate"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('fitengineerBackendV2App.userPurchasedPlans.planEndDate')}
                id="user-purchased-plans-planEndDate"
                name="planEndDate"
                data-cy="planEndDate"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                id="user-purchased-plans-user"
                name="user"
                data-cy="user"
                label={translate('fitengineerBackendV2App.userPurchasedPlans.user')}
                type="select"
                required
              >
                <option value="" key="0" />
                {users
                  ? users.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.login}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <FormText>
                <Translate contentKey="entity.validation.required">This field is required.</Translate>
              </FormText>
              <ValidatedField
                id="user-purchased-plans-productDeliveryStatus"
                name="productDeliveryStatus"
                data-cy="productDeliveryStatus"
                label={translate('fitengineerBackendV2App.userPurchasedPlans.productDeliveryStatus')}
                type="select"
                required
              >
                <option value="" key="0" />
                {productDeliveryStatuses
                  ? productDeliveryStatuses.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.status}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <FormText>
                <Translate contentKey="entity.validation.required">This field is required.</Translate>
              </FormText>
              <ValidatedField
                id="user-purchased-plans-transactionDetails"
                name="transactionDetails"
                data-cy="transactionDetails"
                label={translate('fitengineerBackendV2App.userPurchasedPlans.transactionDetails')}
                type="select"
                required
              >
                <option value="" key="0" />
                {transactionDetails
                  ? transactionDetails.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.paymentTransactionId}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <FormText>
                <Translate contentKey="entity.validation.required">This field is required.</Translate>
              </FormText>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/user-purchased-plans" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default UserPurchasedPlansUpdate;
